import React, { useState, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useLanguage } from '@/contexts/LanguageContext';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import { ChevronLeft, CreditCard, Lock, Dumbbell as DumbbellIcon, Loader2 } from 'lucide-react';
import SubscriptionStatus from '@/components/payment/SubscriptionStatus';
import { STRIPE_PUBLISHABLE_KEY, BASE_URL } from '@/config/api';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const SubscriptionPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const { plan = 'professional', annual = false } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [clientSecret, setClientSecret] = useState<string>('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionError, setSubscriptionError] = useState<string | undefined>();
  const [priceData, setPriceData] = useState<any>(null);
  const [promoCode, setPromoCode] = useState('');
  const [isPromoCodeValid, setIsPromoCodeValid] = useState(false);
  const [discount, setDiscount] = useState<number>(0);
  const [discountDuration, setDiscountDuration] = useState<string | null>(null);
  const [discountDurationMonths, setDiscountDurationMonths] = useState<number | null>(null);
  const [checkingPromo, setCheckingPromo] = useState(false);
  const [isPromoCodeChecked, setIsPromoCodeChecked] = useState(false);



  useEffect(() => {
    if (user?.is_subscribed) {
      navigate('/my-account');
    }
  }, [user, navigate]);

  useEffect(() => {
    const initializeSubscription = async () => {
      try {
        console.log(user.prices.data);
        const price = user.prices.data.find(price => price.active === true);
        if (!price) {
          throw new Error('Price not found');
        }

        const response = await fetch(`${BASE_URL}/api/create-setup-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ priceId: price.id, planName: price.nickname, billingPeriod: annual ? 'annual' : 'monthly' }),
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to initialize subscription');
        }

        setPriceData(price);
        setClientSecret(data.clientSecret);
        if (user && user.email) {
          setEmail(user.email);
        }
        setLoading(false);
      } catch (error) {
        console.error('Failed to initialize subscription:', error);
        setSubscriptionError('Failed to initialize subscription. Please try again later.');
        setSubscriptionStatus('incomplete');
        setLoading(false);
      }
    };

    initializeSubscription();
  }, [plan, annual, user, token]);

  const checkPromoCode = async () => {
    try {
      setCheckingPromo(true);
      const response = await fetch(`${BASE_URL}/api/stripe/check/code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ coupon_id: promoCode }),
      });

      const data = await response.json();
      if (response.ok) {
        console.log(data);
        setIsPromoCodeValid(data.isValid);
        if (!data.isValid) {
          setDiscount(0);
          setDiscountDuration(data.duration || null);
          setDiscountDurationMonths(data.duration_in_months || null);

          toast({
            title: "IMPORTANT",
            description: t('subscription.invalid_promo_code'),
            variant: "destructive",
          });
        } else {
          setDiscount(data.discount || 0);
          setDiscountDuration(data.duration || null);
          setDiscountDurationMonths(data.duration_in_months || null);
          toast({
            title: t('subscription.promo_code_applied'),
            variant: "default",
          });
          if (window.innerWidth < 768) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }
      } else {
        throw new Error(data.error || 'Failed to check promo code');
      }
    } catch (error) {
      console.error('Failed to check promo code:', error);
       toast({
          title: "IMPORTANT",
          description: t('subscription.invalid_promo_code'),
          variant: "destructive",
        });
    } finally {
      setIsPromoCodeChecked(true);
      setCheckingPromo(false);
    }
  };

  const options: StripeElementsOptions = {
  clientSecret,
  appearance: {
    theme: 'stripe',
    variables: {
      colorPrimary: '#8B5CF6',
      colorBackground: '#ffffff',
      colorText: '#1A1F2C',
    },
  },
  loader: 'auto',
  paymentMethodCreation: 'manual', // Add this line
};

  const handleRetry = () => {
    setSubscriptionStatus(null);
    setSubscriptionError(undefined);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 container mx-auto px-4 py-8 flex items-center justify-center">
          <SubscriptionStatus status="processing" />
        </div>
        <Footer />
      </div>
    );
  }

  if (subscriptionStatus) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 container mx-auto px-4 py-8 flex items-center justify-center">
          <SubscriptionStatus
            status={subscriptionStatus}
            errorMessage={subscriptionError}
            onRetry={subscriptionStatus === 'incomplete' ? handleRetry : undefined}
          />
        </div>
        <Footer />
      </div>
    );
  }

  const currentPrice = priceData ? priceData.unit_amount / 100 : 0;
  const annualDiscount = annual ? 20 : 0;
  const totalAmount = annual ? currentPrice * 12 : currentPrice;
  const discountedAmount = discount > 0 ? totalAmount * (1 - discount / 100) : totalAmount;

  const getDiscountDurationText = () => {
    if (!discountDuration) return '';

    switch (discountDuration) {
      case 'once':
        return t('subscription.discount_once'); // ex: "La réduction s'applique une seule fois sur votre premier paiement."
      case 'forever':
        return t('subscription.discount_forever'); // ex: "La réduction s'applique sur tous vos paiements tant que votre abonnement reste actif."
      case 'repeating':
        return t('subscription.discount_repeating', { months: discountDurationMonths || 0 }); // ex: "La réduction s'applique pendant 2 mois."
      default:
        return '';
    }
  };

  //console.log(priceData);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1 py-16">
        <div className="container px-4 md:px-6 pt-8">
          <Button
            variant="ghost"
            className="mb-8 pl-0"
            onClick={() => navigate('/my-account')}
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            {t('common.back')}
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2">
              <div className="mb-8">
                <h1 className="text-3xl font-bold mb-2">{t('subscription.title')}</h1>
                <p className="text-muted-foreground">{t('subscription.subtitle')}</p>
              </div>
              <Card className="p-6">
                <h2 className="text-xl font-medium mb-6">{t('subscription.order_summary')}</h2>

                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mr-4">
                      {plan === 'professional' ? (
                        <img src="https://storage.googleapis.com/sportlogin/frontend/logo-blue.png"/>
                      ) : plan === 'starter' ? (
                        <DumbbellIcon className="h-6 w-6 text-primary" />
                      ) : (
                        <div className="h-6 w-6 text-primary flex items-center justify-center">
                          <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 1v3M12 20v3M4.22 4.22l2.12 2.12m11.32 11.32l2.12 2.12M1 12h3m16 0h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="12" r="5" fill="currentColor"/>
                          </svg>
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="font-medium">
                        {/*priceData?.nickname*/}
                        {t('subscription.premiumAccess')}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {annual ? t('subscription.billed_annually') : t('subscription.billed_monthly')}
                      </p>
                    </div>
                  </div>
                </div>

                <Separator className="my-6" />

                <div className="space-y-2 mb-6 bg-gray-50 p-2 rounded-lg">
                  <div className="flex justify-between font-medium text-lg">
                    <span>{t('subscription.total')}</span>
                    <span>{totalAmount.toFixed(2)} {priceData?.currency?.toUpperCase()}</span>
                  </div>

                  {discount > 0 && (
                    <>
                      <div className="flex justify-between text-sm text-green-600">
                        <span>{t('subscription.discount_applied')} (-{discount}%)</span>
                        <span>-{(totalAmount * discount / 100).toFixed(2)} {priceData?.currency?.toUpperCase()}</span>
                      </div>

                      <div className="flex justify-between font-bold text-lg">
                        <span>{t('subscription.total_after_discount')}</span>
                        <span>{discountedAmount.toFixed(2)} {priceData?.currency?.toUpperCase()}</span>
                      </div>

                      {/* 🟢 Texte explicatif sur la durée */}
                      <p className="text-sm text-gray-600 mt-2">
                        {getDiscountDurationText()}
                      </p>
                    </>
                  )}
                </div>



                <Separator className="my-6" />

                <div>
                  <p className='text-sm'>{t('subscription.infos')}</p>
                </div>
              </Card>
            </div>

            <div className="lg:col-span-3">
              <Card className="mb-8 overflow-hidden">
                {clientSecret ? (
                  <Elements stripe={stripePromise} options={options}>
                    <SubscriptionCheckoutForm
                      email={email}
                      clientSecret={clientSecret}
                      promoCode={promoCode}
                      checkingPromo={checkingPromo}
                      isPromoCodeChecked={isPromoCodeChecked}
                      setPromoCode={setPromoCode}
                      checkPromoCode={checkPromoCode}
                      isPromoCodeValid={isPromoCodeValid}
                      onSubscriptionSuccess={() => setSubscriptionStatus('active')}
                      onSubscriptionError={(error) => {
                        setSubscriptionError(error);
                        setSubscriptionStatus('incomplete');
                      }}
                      onRequiresAction={() => setSubscriptionStatus('requires_action')}
                    />
                  </Elements>
                ) : (
                  <div className="p-6">
                    <p>Loading payment form...</p>
                  </div>
                )}

                <div className="p-4 bg-gray-50 border-t flex items-center justify-center text-sm text-muted-foreground">
                  <Lock className="h-4 w-4 mr-2" />
                  {t('subscription.secure_encryption')}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

// Composant de formulaire de paiement d'abonnement avec les hooks de Stripe
function SubscriptionCheckoutForm({
  email,
  clientSecret,
  promoCode,
  setPromoCode,
  checkPromoCode,
  isPromoCodeValid,
  checkingPromo,
  isPromoCodeChecked,
  onSubscriptionSuccess,
  onSubscriptionError,
  onRequiresAction
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useLanguage();

  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async () => {
  if (!stripe || !elements) {
    return;
  }

  setIsSubmitting(true);

  try {
    // Submit the payment details for validation
    const { error: submitError } = await elements.submit();

    if (submitError) {
      onSubscriptionError(submitError.message || 'An unexpected error occurred');
      console.error('[Submit Error]', submitError);
      setIsSubmitting(false);
      return;
    }

    // Create the payment method after successful submission
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      elements,
      params: {
        billing_details: {
          email: email,
        },
      },
    });

    if (error) {
      onSubscriptionError(error.message || 'An unexpected error occurred');
      console.error('[Subscription Error]', error);
    } else {
      const token = localStorage.getItem('auth-token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${BASE_URL}/api/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          promoCode: isPromoCodeValid ? promoCode : undefined
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create subscription');
      }

      if(data.status === 10) {
        onSubscriptionSuccess();
      }

      if (data.requiresAction) {
        const { error: confirmError } = await stripe.confirmCardPayment(data.clientSecret);
        if (confirmError) {
          onSubscriptionError(confirmError.message || 'An unexpected error occurred');
        } else {
          onSubscriptionSuccess();
        }
      } else {
        onSubscriptionSuccess();
      }
    }
  } catch (e) {
    console.error('Subscription submission error:', e);
    onSubscriptionError(t('subscription.network_error'));
  } finally {
    setIsSubmitting(false);
  }
};


  return (
    <div className="p-6">
      <h2 className="text-xl font-medium mb-6 flex items-center">
        <CreditCard className="mr-2 h-5 w-5 text-muted-foreground" />
        {t('subscription.subscription_details')}
      </h2>

      <div className="mb-4">
        <label htmlFor="promoCode" className="block text-sm font-medium text-gray-700">
          {t('subscription.promo_code')}
        </label>
        <div className="mt-1 flex rounded-md shadow-sm">
          <input
            type="text"
            name="promoCode"
            id="promoCode"
            className="border-gray-300 border rounded-l-md flex-1 block w-full p-2"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
            disabled={checkingPromo}
          />
          <button
            type="button"
            disabled={checkingPromo || !promoCode.trim()}
            onClick={checkPromoCode}
            className={`inline-flex items-center px-4 py-2 border border-l-0 border-gray-300 rounded-r-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
              ${checkingPromo ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
          >
            {checkingPromo ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              </>
            ) : (
              t('subscription.apply')
            )}
          </button>
        </div>
        {isPromoCodeValid && (
          <p className="mt-2 text-sm text-green-600">{t('subscription.promo_code_applied')}</p>
        )}
        {!checkingPromo && !isPromoCodeValid && isPromoCodeChecked && (
          <p className="mt-2 text-sm text-red-600">{t('subscription.invalid_promo_code')}</p>
        )}
      </div>

      <PaymentElement />

      <button
        type="button"
        className="mt-4 w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('payment.processing')}
          </span>
        ) : (
          t('payment.complete_payment')
        )}
      </button>
    </div>
  );
}

export default SubscriptionPage;
